"use client";

import React, { useEffect, useMemo, useState } from "react";
import {
  useBusinessPortfolioEstimate,
  useBusinessPortfolioPerformance,
} from "../hooks/query/businessWalletQueries";
import { Line } from "react-chartjs-2";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Tooltip,
  Filler,
} from "chart.js";
import { Skeleton } from "./ui/skeleton";
import PaymentsInIcon from "../assets/images/businessSuite/paymentIn.svg";
import PaymentsOutIcon from "../assets/images/businessSuite/paymentOut.svg";
import { getMatchMediaQueryBelowTailwindLg } from "../constants/tailwindBreakpoints";
import {
  formatRandsDisplay,
  formatZarChartHighlight,
  formatZarNumberForDisplay,
} from "../utils/zarDisplayFormat";

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Tooltip,
  Filler
);

const TIME_FILTERS = [
  { id: "all", label: "All time" },
  { id: "1d", label: "1D" },
  { id: "1w", label: "1W" },
  { id: "1m", label: "1M" },
  { id: "6m", label: "6M" },
  { id: "1y", label: "1Y" },
];

const MAX_BUSINESS_PERFORMANCE_RANGE_DAYS = 365;

function toYmd(date) {
  return date.toISOString().slice(0, 10);
}

function daysBefore(date, days) {
  const d = new Date(date);
  d.setDate(d.getDate() - days);
  return d;
}

function getDateRangeForTimeFilter(filterId) {
  const end = new Date();
  const endYmd = toYmd(end);
  if (filterId === "all") {
    return {
      startDate: toYmd(daysBefore(end, MAX_BUSINESS_PERFORMANCE_RANGE_DAYS)),
      endDate: endYmd,
    };
  }
  const start = new Date(end);

  if (filterId === "1d") {
    start.setDate(start.getDate() - 1);
  } else if (filterId === "1w") {
    start.setDate(start.getDate() - 7);
  } else if (filterId === "1m") {
    start.setMonth(start.getMonth() - 1);
  } else if (filterId === "6m") {
    start.setMonth(start.getMonth() - 6);
  } else if (filterId === "1y") {
    return {
      startDate: toYmd(daysBefore(end, MAX_BUSINESS_PERFORMANCE_RANGE_DAYS)),
      endDate: endYmd,
    };
  } else {
    return {
      startDate: toYmd(daysBefore(end, MAX_BUSINESS_PERFORMANCE_RANGE_DAYS)),
      endDate: endYmd,
    };
  }
  return { startDate: toYmd(start), endDate: endYmd };
}

function shortAxisLabel(s) {
  if (s == null || s === "") {
    return "";
  }
  const str = String(s);
  if (/^\d{4}-\d{2}-\d{2}/.test(str)) {
    const d = new Date(str.length === 10 ? `${str}T12:00:00` : str);
    if (!Number.isNaN(d.getTime())) {
      return d.toLocaleDateString("en-ZA", {
        day: "numeric",
        month: "short",
      });
    }
  }
  return str.length > 12 ? `${str.slice(0, 12)}…` : str;
}

function toZarNumber(raw) {
  if (raw == null || raw === "") {
    return null;
  }
  const n = Number(raw);
  return Number.isFinite(n) ? n : null;
}

function formatZarMagnitude(raw) {
  const n = toZarNumber(raw);
  if (n == null) {
    return null;
  }
  return formatZarNumberForDisplay(Math.abs(n));
}

/** + for net inflow, − for net reversal (per API). Empty at zero. */
function signInflow(n) {
  if (n == null || !Number.isFinite(n)) {
    return "";
  }
  if (n > 0) {
    return "+";
  }
  if (n < 0) {
    return "−";
  }
  return "";
}

/** − when withdrawals are positive; + if API reports a negative. Empty at zero. */
function signOutflow(n) {
  if (n == null || !Number.isFinite(n)) {
    return "";
  }
  if (n > 0) {
    return "−";
  }
  if (n < 0) {
    return "+";
  }
  return "";
}

const CHART_FONT_FAMILY = "PlusJakartaSans, sans-serif";

function formatYTick(value) {
  const n = Number(value);
  if (n >= 1000) {
    const k = n / 1000;
    return k % 1 === 0 ? `${k.toFixed(0)}.0k` : `${k.toFixed(1)}k`;
  }
  return `${n}`;
}

export default function BusinessSuitePerformanceCard({ businessId = null }) {
  const [compactChart, setCompactChart] = useState(false);

  useEffect(() => {
    const mql = window.matchMedia(getMatchMediaQueryBelowTailwindLg());
    const sync = () => setCompactChart(mql.matches);
    sync();
    mql.addEventListener("change", sync);
    return () => mql.removeEventListener("change", sync);
  }, []);

  const [activeFilter, setActiveFilter] = useState("all");
  const { data: businessEst, isLoading: isEstimateLoading } =
    useBusinessPortfolioEstimate(businessId);

  const performanceParams = useMemo(() => {
    if (!businessId) {
      return undefined;
    }
    return getDateRangeForTimeFilter(activeFilter);
  }, [businessId, activeFilter]);

  const { data: performance, isLoading: isPerformanceLoading } =
    useBusinessPortfolioPerformance(businessId, performanceParams);

  const showFullPerformanceSkeleton =
    Boolean(businessId) && (isEstimateLoading || isPerformanceLoading);

  const { chartLabels, chartValues, yMax, highlightIndex } = useMemo(() => {
    const pl = performance?.labels;
    const pv = performance?.values;
    if (
      businessId &&
      Array.isArray(pl) &&
      Array.isArray(pv) &&
      pl.length > 0 &&
      pl.length === pv.length
    ) {
      let labels = pl;
      let values = pv.map((v) => Number(v) || 0);
      if (labels.length === 1) {
        labels = [pl[0], pl[0]];
        values = [values[0], values[0]];
      }
      const maxV = Math.max(1, ...values);
      let hi = 0;
      let best = -Infinity;
      values.forEach((val, i) => {
        if (val > best) {
          best = val;
          hi = i;
        }
      });
      return {
        chartLabels: labels,
        chartValues: values,
        yMax: maxV * 1.15,
        highlightIndex: hi,
      };
    }
    return {
      chartLabels: [],
      chartValues: [],
      yMax: 100,
      highlightIndex: 0,
    };
  }, [businessId, performance?.labels, performance?.values]);

  const displayLabels = useMemo(
    () => chartLabels.map((lab) => shortAxisLabel(lab)),
    [chartLabels]
  );

  const highlightText = useMemo(() => {
    if (!chartValues.length) {
      return null;
    }
    const v = chartValues.slice(highlightIndex, highlightIndex + 1)[0];
    if (v == null) {
      return null;
    }
    return formatZarChartHighlight(v);
  }, [chartValues, highlightIndex]);

  const highlightPlugin = useMemo(
    () => ({
      id: "businessSuiteCashFlowHighlight",
      afterDatasetsDraw: (chart) => {
        if (!highlightText) {
          return;
        }
        const meta = chart.getDatasetMeta(0);
        const segments = meta.data;
        const point = Array.isArray(segments)
          ? segments.slice(highlightIndex, highlightIndex + 1)[0]
          : undefined;
        if (!point) {
          return;
        }

        const { ctx } = chart;
        const { x, y } = point.getProps(["x", "y"], true);

        ctx.save();
        ctx.font = `600 12px ${CHART_FONT_FAMILY}`;
        ctx.fillStyle = "#FFFFFF";
        ctx.textAlign = "center";
        ctx.textBaseline = "bottom";
        ctx.fillText(highlightText, x, y - 12);

        ctx.beginPath();
        ctx.arc(x, y, 6, 0, 2 * Math.PI);
        ctx.fillStyle = "#FFFFFF";
        ctx.fill();
        ctx.lineWidth = 2;
        ctx.strokeStyle = "rgba(255,255,255,0.5)";
        ctx.stroke();
        ctx.restore();
      },
    }),
    [highlightIndex, highlightText]
  );

  const chartData = useMemo(
    () => ({
      labels: displayLabels,
      datasets: [
        {
          label: "Cash flow",
          data: chartValues,
          fill: true,
          borderColor: "#FFFFFF",
          backgroundColor: (context) => {
            const { chart } = context;
            const { ctx, chartArea } = chart;
            if (!chartArea) {
              return "rgba(255,255,255,0.12)";
            }
            const gradient = ctx.createLinearGradient(
              0,
              chartArea.top,
              0,
              chartArea.bottom
            );
            gradient.addColorStop(0, "rgba(255,255,255,0.35)");
            gradient.addColorStop(1, "rgba(255,255,255,0)");
            return gradient;
          },
          tension: 0.4,
          borderWidth: 2,
          pointRadius: 0,
          pointHoverRadius: 4,
        },
      ],
    }),
    [chartValues, displayLabels]
  );

  const chartOptions = useMemo(() => {
    const last = Math.max(0, displayLabels.length - 1);
    const tickSize = compactChart ? 9 : 11;
    const yTickSize = compactChart ? 10 : 11;
    return {
      responsive: true,
      maintainAspectRatio: false,
      layout: {
        padding: compactChart
          ? { left: 0, right: 2, top: 4, bottom: 4 }
          : { left: 4, right: 8, top: 8, bottom: 8 },
      },
      font: {
        family: CHART_FONT_FAMILY,
      },
      interaction: {
        mode: "index",
        intersect: false,
      },
      plugins: {
        legend: { display: false },
        tooltip: {
          enabled: true,
          backgroundColor: "rgba(30, 20, 50, 0.92)",
          titleColor: "#fff",
          bodyColor: "#fff",
          borderColor: "rgba(255,255,255,0.2)",
          borderWidth: 1,
          padding: 10,
          displayColors: false,
          titleFont: { family: CHART_FONT_FAMILY, size: 12 },
          bodyFont: { family: CHART_FONT_FAMILY, size: 12 },
          callbacks: {
            label: (ctx) => {
              const y = Number(ctx.parsed.y);
              return Number.isFinite(y) ? formatRandsDisplay(y) : "";
            },
          },
        },
      },
      scales: {
        x: {
          grid: {
            display: true,
            drawTicks: false,
            color: "rgba(255,255,255,0.35)",
            lineWidth: 2,
            borderCapStyle: "round",
            borderDash: [1, 8],
          },
          ticks: {
            color: "#FFFFFF",
            maxRotation: compactChart ? 0 : 40,
            minRotation: 0,
            autoSkip: true,
            maxTicksLimit: compactChart ? 6 : 12,
            font: (context) => ({
              family: CHART_FONT_FAMILY,
              size: tickSize,
              weight: context.index === last ? "700" : "400",
            }),
          },
          border: {
            display: false,
          },
        },
        y: {
          beginAtZero: true,
          suggestedMax: yMax,
          grid: {
            display: false,
          },
          ticks: {
            color: "#FFFFFF",
            font: { family: CHART_FONT_FAMILY, size: yTickSize },
            callback: (value) => formatYTick(value),
            padding: compactChart ? 4 : 8,
          },
          border: {
            display: false,
          },
        },
      },
    };
  }, [compactChart, displayLabels.length, yMax]);

  const paymentsIn = useMemo(() => {
    const raw =
      performance?.totalDepositInZar ?? businessEst?.totalDepositInZar;
    const n = toZarNumber(raw);
    if (n == null) {
      return null;
    }
    return {
      sign: signInflow(n),
      amount: formatZarMagnitude(raw),
    };
  }, [performance?.totalDepositInZar, businessEst?.totalDepositInZar]);

  const paymentsOut = useMemo(() => {
    const raw =
      performance?.totalWithdrawInZar ?? businessEst?.totalWithdrawInZar;
    const n = toZarNumber(raw);
    if (n == null) {
      return null;
    }
    return {
      sign: signOutflow(n),
      amount: formatZarMagnitude(raw),
    };
  }, [performance?.totalWithdrawInZar, businessEst?.totalWithdrawInZar]);

  const showEmptyChart =
    businessId && !isPerformanceLoading && chartValues.length === 0;

  return (
    <div className="w-full rounded-2xl border border-[#EBE9EF] bg-white p-4 sm:p-6 md:p-7">
      <h2 className="text-[18px] sm:text-[20px] font-semibold text-[#6D6584] mb-3 md:mb-5">
        Performance
      </h2>

      <div className="rounded-2xl p-3 sm:p-5 md:p-6 text-white overflow-hidden bg-business-suite-performance-card">
        {showFullPerformanceSkeleton ? (
          <div>
            <div className="hide-scrollbar flex gap-2 sm:gap-3 md:gap-4 mb-4 md:mb-6 overflow-x-auto pb-1 -mx-0.5 px-0.5">
              {TIME_FILTERS.map((f) => (
                <Skeleton
                  key={f.id}
                  className="h-10 sm:h-12 min-w-[76px] sm:min-w-[96px] md:min-w-[108px] shrink-0 rounded-full bg-white/20"
                />
              ))}
            </div>
            <div className="flex flex-col xl:flex-row xl:items-stretch gap-4 md:gap-6">
              <div className="min-w-0 w-full flex flex-col xl:flex-1 xl:min-h-0">
                <Skeleton className="h-4 w-24 mb-2 sm:mb-3 bg-white/25" />
                <div className="w-full min-h-0">
                  <Skeleton className="h-[200px] sm:h-[220px] md:h-[240px] w-full rounded-lg bg-white/20" />
                </div>
              </div>
              <div className="flex flex-col gap-3 xl:w-[285px] shrink-0">
                <Skeleton className="h-[108px] w-full rounded-xl bg-white/20" />
                <Skeleton className="h-[108px] w-full rounded-xl bg-white/20" />
              </div>
            </div>
          </div>
        ) : (
          <>
            <div className="hide-scrollbar flex gap-2 sm:gap-3 md:gap-4 mb-4 md:mb-6 overflow-x-auto pb-1 -mx-0.5 px-0.5">
              {TIME_FILTERS.map((f) => {
                const isActive = activeFilter === f.id;
                return (
                  <button
                    key={f.id}
                    type="button"
                    onClick={() => setActiveFilter(f.id)}
                    className={`shrink-0 rounded-full px-2.5 sm:px-3.5 h-10 sm:h-12 min-w-[76px] sm:min-w-[88px] md:min-w-[100px] text-[12px] sm:text-[14px] font-semibold transition-colors snap-start ${
                      isActive
                        ? "bg-white text-[#33125F]"
                        : "bg-transparent text-white border border-white/80 hover:bg-white/10"
                    }`}
                  >
                    {f.label}
                  </button>
                );
              })}
            </div>

            <div className="flex flex-col xl:flex-row xl:items-stretch gap-4 md:gap-6">
              <div className="min-w-0 w-full flex flex-col xl:flex-1 xl:min-h-0">
                <p className="text-[14px] sm:text-[16px] font-semibold text-white mb-2 sm:mb-3">
                  Cash flow
                </p>
                <div className="w-full min-h-0">
                  <div className="relative h-[200px] w-full sm:h-[220px] md:h-[240px]">
                    {showEmptyChart && (
                      <p className="h-full w-full flex items-center justify-center text-sm text-white/80 text-center px-4">
                        No cash flow data for this period.
                      </p>
                    )}
                    {!showEmptyChart && (
                      <Line
                        data={chartData}
                        options={chartOptions}
                        plugins={highlightText ? [highlightPlugin] : []}
                      />
                    )}
                  </div>
                </div>
              </div>

              <div className="flex flex-col gap-3 xl:w-[285px] shrink-0">
                <div className="rounded-xl border border-white/25 bg-white/15 backdrop-blur-md px-4 py-4 relative h-[108px]">
                  <img
                    src={PaymentsInIcon}
                    alt="Payments in"
                    className="absolute -top-1 right-0 w-[50px] h-[50px]"
                  />
                  <p className="text-[13px] text-white mb-2 mt-1 pr-8">
                    Payments in
                  </p>
                  {paymentsIn ? (
                    <p className="text-[32px] font-semibold tracking-tight">
                      {paymentsIn.sign ? (
                        <span className="text-[32px] font-semibold text-white/50">
                          {paymentsIn.sign}
                        </span>
                      ) : null}
                      {paymentsIn.sign ? " " : null}
                      <span className="text-[32px] font-semibold text-white/50">
                        R
                      </span>{" "}
                      {paymentsIn.amount}
                    </p>
                  ) : (
                    <p className="text-[32px] font-semibold text-white/50">—</p>
                  )}
                </div>
                <div className="rounded-xl border border-white/25 bg-white/15 backdrop-blur-md px-4 py-4 relative h-[108px]">
                  <img
                    src={PaymentsOutIcon}
                    alt="Payments out"
                    className="absolute -top-1 right-0 w-[50px] h-[50px]"
                  />
                  <p className="text-[13px] text-white mb-2 mt-1 pr-8">
                    Payments out
                  </p>
                  {paymentsOut ? (
                    <p className="text-[32px] font-semibold tracking-tight">
                      {paymentsOut.sign ? (
                        <span className="text-[32px] font-semibold text-white/50">
                          {paymentsOut.sign}
                        </span>
                      ) : null}
                      {paymentsOut.sign ? " " : null}
                      <span className="text-[32px] font-semibold text-white/50">
                        R
                      </span>{" "}
                      {paymentsOut.amount}
                    </p>
                  ) : (
                    <p className="text-[32px] font-semibold text-white/50">—</p>
                  )}
                </div>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
